import { useState, useEffect, useCallback } from "react";
import { KeyRound, ShieldAlert, Pencil, Save, X, Mail, Phone, CornerUpLeft, UserPlus, Loader2, UserX, UserCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABEL = { employee: "Handlowiec", production: "Produkcja", admin: "Administrator", haft: "Haft" };
const ROLE_BADGE = {
  admin: "border-rose-500/30 bg-rose-500/15 text-rose-300",
  production: "border-sky-500/30 bg-sky-500/15 text-sky-300",
  employee: "border-amber-500/30 bg-amber-500/15 text-amber-300",
};

export const Team = ({ onChanged }) => {
  const confirmDialog = useConfirm();
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [nu, setNu] = useState({ name: "", role: "employee", email: "", phone: "" });
  const [adding, setAdding] = useState(false);
  const [replyTo, setReplyTo] = useState("");
  const [savedReplyTo, setSavedReplyTo] = useState("");
  const [savingReply, setSavingReply] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/users/all"); setAllUsers(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    api.get("/settings/email").then((r) => { setReplyTo(r.data.reply_to || ""); setSavedReplyTo(r.data.reply_to || ""); }).catch(() => {});
  }, []);

  const refresh = () => { loadAll(); onChanged?.(); };

  const addUser = async () => {
    if (!nu.name.trim()) return toast.error("Podaj imię i nazwisko");
    setAdding(true);
    try {
      await api.post("/users", { name: nu.name.trim(), role: nu.role, email: nu.email.trim(), phone: nu.phone.trim() });
      toast.success(`Dodano użytkownika (${ROLE_LABEL[nu.role]}). Hasło startowe: Empro123`);
      setNu({ name: "", role: "employee", email: "", phone: "" });
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setAdding(false); }
  };

  const changeRole = async (u, role) => {
    if (u.role === role) return;
    if (!(await confirmDialog({ title: "Zmienić rolę?", description: `Zmienić rolę „${u.name}" z ${ROLE_LABEL[u.role]} na ${ROLE_LABEL[role]}?`, confirmText: "Zmień rolę" }))) return;
    try {
      await api.put(`/users/${u.id}/role`, { role });
      toast.success(`Rola zmieniona: ${u.name} → ${ROLE_LABEL[role]}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleActive = async (u) => {
    const next = u.active === false;
    const ok = await confirmDialog(next
      ? { title: "Aktywować konto?", description: `Przywrócić dostęp użytkownikowi „${u.name}"? Będzie mógł znów się logować.`, confirmText: "Aktywuj" }
      : { title: "Dezaktywować konto?", description: `Zablokować logowanie użytkownikowi „${u.name}"? Jego historia (klienci, zamówienia, zdjęcia) zostanie zachowana. Konto można później aktywować.`, confirmText: "Dezaktywuj", danger: true });
    if (!ok) return;
    try {
      await api.patch(`/users/${u.id}/active`, { active: next });
      toast.success(next ? `Konto aktywowane: ${u.name}` : `Konto dezaktywowane: ${u.name}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleDual = async (u) => {
    const next = !u.dual_role;
    const ok = await confirmDialog(next
      ? { title: "Włączyć podwójną rolę?", description: `„${u.name}" zyska w nagłówku przełącznik „Widok: Handlowiec / Administrator" i będzie mógł prowadzić własnych klientów oraz dodawać zamówienia na siebie.`, confirmText: "Włącz" }
      : { title: "Wyłączyć podwójną rolę?", description: `„${u.name}" straci widok handlowca i pozostanie samym administratorem.`, confirmText: "Wyłącz" });
    if (!ok) return;
    try {
      await api.patch(`/users/${u.id}/dual-role`, { dual: next });
      toast.success(next ? `Włączono podwójną rolę: ${u.name}` : `Wyłączono podwójną rolę: ${u.name}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doReset = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${confirm.id}/reset-pin`);
      toast.success(`Hasło użytkownika ${data.name} zresetowane do Empro123. Przy następnym logowaniu ustawi własne hasło.`);
      setConfirm(null);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const startEdit = (u) => setEdit({ id: u.id, email: u.email || "", phone: u.phone || "" });
  const saveContact = async () => {
    setSavingId(edit.id);
    try {
      await api.put(`/users/${edit.id}/contact`, { email: edit.email.trim(), phone: edit.phone.trim() });
      toast.success("Zapisano dane kontaktowe");
      setEdit(null);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingId(null); }
  };

  const saveReplyTo = async () => {
    setSavingReply(true);
    try {
      const { data } = await api.put("/settings/email", { reply_to: replyTo.trim() });
      setSavedReplyTo(data.reply_to); setReplyTo(data.reply_to);
      toast.success(data.reply_to ? "Zapisano adres zwrotny" : "Adres zwrotny wyczyszczony");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingReply(false); }
  };

  return (
    <div className="space-y-4" data-testid="team-view">
      <div className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-400/5 px-4 py-3">
        <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0" />
        <p className="text-sm text-muted-foreground">Zarządzaj użytkownikami i ich rolami. Reset hasła ustawia hasło <b className="text-foreground">Empro123</b> (zmiana wymagana przy pierwszym logowaniu). Uzupełnij e-mail, aby handlowiec otrzymywał powiadomienia o zmianie statusu zamówienia.</p>
      </div>

      {/* Add user */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="user-add-form">
        <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" />Nowy użytkownik</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1.5fr_1fr_auto]">
          <Input data-testid="user-name-input" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Imię i nazwisko" className="h-9" />
          <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
            <SelectTrigger data-testid="user-role-select" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Handlowiec</SelectItem>
              <SelectItem value="production">Produkcja</SelectItem>
              <SelectItem value="haft">Haft</SelectItem>
              <SelectItem value="admin">Administrator</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="user-email-input" type="email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} placeholder="E-mail (opcjonalnie)" className="h-9" />
          <Input data-testid="user-phone-input" value={nu.phone} onChange={(e) => setNu({ ...nu, phone: e.target.value })} placeholder="Telefon" className="h-9" />
          <Button data-testid="user-add-button" onClick={addUser} disabled={adding} className="h-9 gap-1.5 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold shrink-0"><UserPlus className="h-4 w-4" />Dodaj</Button>
        </div>
      </div>

      {/* Reply-To */}
      <div className="rounded-xl border border-border bg-card px-4 py-3 space-y-2" data-testid="email-reply-to-card">
        <div className="flex items-center gap-2">
          <CornerUpLeft className="h-4 w-4 text-sky-400" />
          <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Adres zwrotny (Reply-To) powiadomień</p>
        </div>
        <p className="text-xs text-muted-foreground">Na ten adres handlowiec odpisze, klikając „Odpowiedz" w powiadomieniu e-mail od EMPRO WORKWEAR. Zostaw puste, aby wyłączyć.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="email-reply-to-input" type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="np. biuro@empro.pro" className="h-9 w-[260px]" />
          <Button data-testid="email-reply-to-save" size="sm" disabled={savingReply || replyTo.trim() === savedReplyTo} onClick={saveReplyTo} className="h-9 gap-1.5 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Save className="h-4 w-4" />Zapisz</Button>
          {savedReplyTo && <span className="text-xs text-emerald-400">Aktywny: {savedReplyTo}</span>}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="team-table">
            <thead className="bg-secondary text-muted-foreground font-mono text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Użytkownik</th>
                <th className="text-left px-3 py-2.5">Rola</th>
                <th className="text-left px-3 py-2.5">E-mail</th>
                <th className="text-left px-3 py-2.5">Telefon</th>
                <th className="text-left px-3 py-2.5">Status hasła</th>
                <th className="px-3 py-2.5 text-right sticky right-0 bg-secondary border-l border-border">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.map((u) => {
                const isEditing = edit?.id === u.id;
                return (
                  <tr key={u.id} data-testid={`team-row-${u.id}`} className={`border-t border-border hover:bg-accent/50 align-top ${u.active === false ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 font-medium">{u.name}{u.active === false && <span data-testid={`team-inactive-badge-${u.id}`} className="ml-2 inline-flex rounded-md border border-rose-500/30 bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-300">Nieaktywny</span>}</td>
                    <td className="px-3 py-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger data-testid={`team-role-select-${u.id}`} className="h-8 w-[150px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employee">Handlowiec</SelectItem>
                          <SelectItem value="production">Produkcja</SelectItem>
                          <SelectItem value="haft">Haft</SelectItem>
                          <SelectItem value="admin">Administrator</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className={`ml-0 mt-1 inline-flex rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE[u.role] || ""}`}>{ROLE_LABEL[u.role] || u.role}</span>
                      {u.role === "admin" && (
                        <button data-testid={`team-dual-toggle-${u.id}`} onClick={() => toggleDual(u)}
                          className={`mt-1.5 flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${u.dual_role ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" : "border-border bg-secondary text-muted-foreground hover:text-foreground"}`}>
                          <Users className="h-3 w-3" />{u.dual_role ? "Podwójna rola: WŁ" : "Podwójna rola: WYŁ"}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {isEditing
                        ? <Input data-testid={`team-email-input-${u.id}`} type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="np. jan@empro.pro" className="h-8 text-xs w-[200px]" />
                        : (u.email ? <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Mail className="h-3.5 w-3.5 text-sky-400" />{u.email}</span> : <span className="text-muted-foreground/50">—</span>)}
                    </td>
                    <td className="px-3 py-3">
                      {isEditing
                        ? <Input data-testid={`team-phone-input-${u.id}`} value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} placeholder="np. 600 100 200" className="h-8 text-xs w-[150px]" />
                        : (u.phone ? <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Phone className="h-3.5 w-3.5 text-emerald-400" />{u.phone}</span> : <span className="text-muted-foreground/50">—</span>)}
                    </td>
                    <td className="px-3 py-3">
                      {u.must_change_pin
                        ? <span className="inline-flex items-center rounded-md border border-blue-500/30 bg-blue-500/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-blue-300">Wymaga zmiany (Empro123)</span>
                        : <span className="inline-flex items-center rounded-md border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-emerald-300">Własne hasło</span>}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap sticky right-0 bg-card border-l border-border">
                      {isEditing ? (
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`team-contact-save-${u.id}`} size="sm" disabled={savingId === u.id} onClick={saveContact} className="gap-1.5 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Save className="h-4 w-4" />Zapisz</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEdit(null)}><X className="h-4 w-4" /></Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1.5">
                          <Button data-testid={`team-contact-edit-${u.id}`} size="sm" variant="outline" className="gap-1.5 font-semibold" onClick={() => startEdit(u)}><Pencil className="h-4 w-4" />Edytuj</Button>
                          <Button data-testid={`team-reset-pin-${u.id}`} size="sm" variant="outline" className="gap-1.5 font-semibold" onClick={() => setConfirm(u)}><KeyRound className="h-4 w-4" />Resetuj hasło</Button>
                          {u.active === false
                            ? <Button data-testid={`team-activate-${u.id}`} size="sm" variant="outline" className="gap-1.5 font-semibold text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" onClick={() => toggleActive(u)}><UserCheck className="h-4 w-4" />Aktywuj</Button>
                            : <Button data-testid={`team-deactivate-${u.id}`} size="sm" variant="outline" className="gap-1.5 font-semibold text-rose-400 border-rose-500/40 hover:bg-rose-500/10" onClick={() => toggleActive(u)}><UserX className="h-4 w-4" />Dezaktywuj</Button>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="team-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Zresetować hasło?</AlertDialogTitle>
            <AlertDialogDescription>
              Hasło użytkownika <b>{confirm?.name}</b> zostanie ustawione na <b>Empro123</b>. Przy następnym logowaniu użytkownik będzie musiał ustawić własne hasło. Tej operacji nie można cofnąć.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-reset-cancel">Anuluj</AlertDialogCancel>
            <AlertDialogAction data-testid="team-reset-confirm" disabled={busy} onClick={doReset} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">Resetuj hasło</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
